import { useAuth0 } from '@auth0/auth0-react';
import useTopicOrClientWord from 'components/Dashboard/hooks/useTopicOrClientWord';
import parliamentarianUsers from 'components/Dashboard/SidebarElements/parliamentarianUsers';
import useIsMobile from 'components/Dashboard/utilities/useIsMobile';
import { store } from 'components/Store';
import { useContext } from 'react';
import getAppSite from 'utils/getAppSite';
import LocalStorage from 'utils/LocalStorage';

const useDashboardUrlCondition = () => {
  const globalState = useContext(store);
  const { state } = globalState;

  const { user } = useAuth0();
  const isMobile = useIsMobile();
  const { agencyUser, newAgencyPlan } = useTopicOrClientWord();

  const { activePlan, defaultUrl: storeDefaultUrl } = state ?? {};
  const { showDashboard, enableBriefingContentGeneration } = activePlan ?? {};
  const allowedDashboardUsers = enableBriefingContentGeneration && showDashboard;

  const allowedUrls = getAllowedUrls({ activePlan, user, agencyUser, newAgencyPlan, isMobile });
  const storedDefaultUrl = storeDefaultUrl ?? LocalStorage.getItem('defaultUrl');
  const storedDefaultUrlFound = allowedUrls.find((url) => url === storedDefaultUrl);

  const defaultUrl = storedDefaultUrlFound || (allowedDashboardUsers ? 'dashboard' : 'monitor-inbox');

  return { defaultUrl, allowedDashboardUsers, showDashboard };
};

const getAllowedUrls = ({ activePlan, user, agencyUser, newAgencyPlan, isMobile }) => {
  const { code, enableAnalytics, enableReports, showDashboard, enableBriefingContentGeneration, teamName } =
    activePlan ?? {};

  const parliamentarianUser = parliamentarianUsers(code);
  const allowedDashboardUsers = enableBriefingContentGeneration && showDashboard;
  const hideInfluenceSection =
    parliamentarianUser || (teamName === 'NCA' && user?.email !== 'james.mitchell@nca.gov.uk');

  const urls = [
    {
      condition: allowedDashboardUsers,
      pathname: 'dashboard',
    },
    {
      condition: true,
      pathname: 'monitor-inbox',
    },
    {
      condition: parliamentarianUser,
      pathname: 'campaigns',
    },
    {
      condition: !hideInfluenceSection,
      pathname: 'people/directory',
    },
    {
      condition: parliamentarianUser,
      pathname: parliamentarianUser ? 'policymakers' : 'people/stakeholder-mapping',
    },
    {
      condition: true,
      disabled: !enableAnalytics,
      pathname: 'analytics/dashboard',
    },
    {
      condition: !agencyUser,
      disabled: !enableReports,
      pathname: 'reports',
    },
    {
      condition: !parliamentarianUser && getAppSite() !== 'usa',
      pathname: 'brief-policymakers',
    },
    {
      condition: parliamentarianUser,
      pathname: 'ai-assistant',
    },
    {
      condition: isMobile && !newAgencyPlan && getAppSite() !== 'usa',
      pathname: 'calendar',
    },
  ];
  return urls.filter((url) => url.condition && !url.disabled).map((url) => url.pathname);
};

export default useDashboardUrlCondition;
